import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { fetchDashboardKPICards } from "../../../API_Service/Dashboard_Slice";
import { companyID, KPICard, InlineAlert } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";

const KPICards = () => {
  const dispatch = useDispatch();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = useCallback(() => {
    if (!companyID) {
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    dispatch(fetchDashboardKPICards({ companyID }))
      .then((result) => {
        if (result.payload && result.payload.length > 0) {
          setStats(result.payload[0]);
          setError(null);
        } else if (result.payload && !Array.isArray(result.payload)) {
          setStats(result.payload);
        }
      })
      .catch((err) => {
        setError(err?.message || "Failed to load KPI cards");
      })
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useDashboardSignalR(loadStats);

  const cards = stats
    ? [
        {
          icon: "assignment",
          label: "Total Projects",
          value: stats.TotalProjects,
          iconColor: "text-blue-600",
          gradientFrom: "from-blue-500",
          gradientTo: "to-blue-600",
        },
        {
          icon: "trending_up",
          label: "Running Projects",
          value: stats.RunningProjects,
          iconColor: "text-green-600",
          gradientFrom: "from-green-500",
          gradientTo: "to-emerald-600",
        },
        {
          icon: "schedule",
          label: "Upcoming Projects",
          value: stats.UpcomingProjects,
          iconColor: "text-purple-600",
          gradientFrom: "from-purple-500",
          gradientTo: "to-purple-600",
        },
        {
          icon: "stop_circle",
          label: "Completed Projects",
          value: stats.CompletedProjects,
          iconColor: "text-orange-600",
          gradientFrom: "from-orange-500",
          gradientTo: "to-red-600",
        },
        {
          icon: "access_time",
          label: "Billable Hours",
          value: stats.TotalBillableHours,
          iconColor: "text-indigo-600",
          gradientFrom: "from-indigo-500",
          gradientTo: "to-indigo-600",
        },
        {
          icon: "hourglass_empty",
          label: "Non-Billable Hours",
          value: stats.TotalNonBillableHours,
          iconColor: "text-red-600",
          gradientFrom: "from-red-500",
          gradientTo: "to-pink-600",
        },
        {
          icon: "check_circle",
          label: "Completion Percentage",
          value: `${stats.ProjectCompletionPercentage.toFixed(2)}%`,
          iconColor: "text-emerald-600",
          gradientFrom: "from-emerald-500",
          gradientTo: "to-teal-600",
        },
      ]
    : [];

  if (error) {
    return <InlineAlert type="error" message={`Error loading KPI cards: ${error}`} />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, index) => (
        <KPICard
          key={index}
          icon={card.icon}
          label={card.label}
          value={card.value}
          iconColor={card.iconColor}
          gradientFrom={card.gradientFrom}
          gradientTo={card.gradientTo}
          isLoading={isLoading}
        />
      ))}
      {isLoading && cards.length === 0 && (
        <>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="relative bg-white rounded-lg border border-slate-200/80 p-4 animate-pulse">
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-slate-200 rounded-l-lg"></div>
              <div className="flex items-start justify-between gap-3 pl-1.5">
                <div className="flex-1">
                  <div className="h-2.5 bg-slate-200 rounded w-2/3 mb-2.5"></div>
                  <div className="h-7 bg-slate-200 rounded w-1/2"></div>
                </div>
                <div className="w-9 h-9 bg-slate-200 rounded-md"></div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default KPICards;
